import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSEO } from "@/hooks/useSEO";
import { useTemplateSelection } from "@/hooks/useTemplateSelection";
import { useMercadoPagoInstagramEscape } from "@/hooks/useMercadoPagoInstagramEscape";
import profileImage from "@/assets/profile-delfu.jpg";
import { Instagram, MapPin, Youtube } from "lucide-react";
import { BRAND_HANDLE, BRAND_PERSON_NAME } from "@/lib/brandConfig";
import {
  getServerTemplateConfig,
  workerCtaNeedsDefensiveFetch,
} from "@/lib/serverTemplateDetection";
import { useFreshCtaFlags } from "@/hooks/useFreshCtaFlags";
import MuxVideoPlayerWithCTA from "@/components/MuxVideoPlayerWithCTA";
import {
  isNeighborhoodBetaMejoresAmigos,
  resolveNeighborhoodBetaVideoCta,
  type NeighborhoodVideoCtaContext,
} from "@/config/videoCTAConfig";

const socialLinks = [
  {
    icon: Instagram,
    label: "Instagram",
    href: `https://instagram.com/${BRAND_HANDLE}`,
  },
  {
    icon: Youtube,
    label: "YouTube",
    href: `https://www.youtube.com/@${BRAND_HANDLE}`,
  },
];

const Video = () => {
  useSEO("video");
  const { t, i18n } = useTranslation();
  const {
    country,
    province,
    neighborhood,
    neighborhoodCity,
    showBestFriends,
    showBirthday,
    isArgentina,
  } = useTemplateSelection();

  const serverConfig = useMemo(() => getServerTemplateConfig(), []);
  const city = serverConfig?.city || neighborhoodCity;

  const { interceptMercadoPagoCta, overlay: mpOverlay } = useMercadoPagoInstagramEscape({
    surface: "video_page_neighborhood_beta",
    countryCode: country || "AR",
    language: "es",
  });

  const needsDefensiveCtaFetch = workerCtaNeedsDefensiveFetch(serverConfig);
  const { freshFlags } = useFreshCtaFlags({
    enabled: isArgentina,
    skipInitialFetch: isArgentina && !needsDefensiveCtaFetch,
    countryCode: country || "AR",
    templateConfig: serverConfig,
  });

  const neighborhoodCtx = useMemo<NeighborhoodVideoCtaContext>(
    () => ({
      country: country || "AR",
      province,
      city: city || neighborhoodCity,
      neighborhood,
      neighborhoodCity,
      showBestFriends: freshFlags?.showBestFriends ?? showBestFriends,
      showBirthday: freshFlags?.showBirthday ?? showBirthday,
      ctaUrl: freshFlags?.ctaUrl ?? serverConfig?.ctaUrl,
      ctaLabel: freshFlags?.ctaLabel ?? serverConfig?.ctaLabel,
      mejoresAmigosTiers:
        freshFlags?.mejoresAmigosTiers ?? serverConfig?.mejoresAmigosTiers,
    }),
    [
      country,
      province,
      city,
      neighborhood,
      neighborhoodCity,
      showBestFriends,
      showBirthday,
      freshFlags,
      serverConfig?.ctaUrl,
      serverConfig?.ctaLabel,
      serverConfig?.mejoresAmigosTiers,
    ],
  );

  const betaVideo = useMemo(
    () =>
      resolveNeighborhoodBetaVideoCta(
        neighborhoodCtx,
        (i18n.language?.split("-")[0] as "es" | "en") || "es",
      ),
    [neighborhoodCtx, i18n.language],
  );

  const showBeta = isNeighborhoodBetaMejoresAmigos(neighborhoodCtx);
  const betaLabel =
    freshFlags?.geo?.betaLabel ??
    betaVideo?.neighborhoodBetaLabel ??
    (neighborhood ? `Barrio ${neighborhood}` : null);

  const tierMedium = neighborhoodCtx.mejoresAmigosTiers?.medium;
  const tierHigh = neighborhoodCtx.mejoresAmigosTiers?.high;
  const fallbackUrl = neighborhoodCtx.ctaUrl?.trim() || betaVideo?.ctaUrl;

  return (
    <div className="min-h-screen bg-background">
      <main className="max-w-md mx-auto px-5 py-12 md:py-16">
        <header className="text-center mb-10 animate-fade-in">
          <div className="relative inline-block mb-8">
            <div className="absolute -inset-1 rounded-full bg-gradient-to-br from-border via-muted to-border opacity-60" />
            <div className="relative w-28 h-28 rounded-full overflow-hidden ring-2 ring-background shadow-lg">
              <img
                src={profileImage}
                alt={`${BRAND_PERSON_NAME} - Video content creator profile photo`}
                loading="lazy"
                width={112}
                height={112}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-transparent via-background/10 to-transparent pointer-events-none" />
          </div>

          <h1 className="text-3xl md:text-4xl font-display font-medium text-foreground tracking-wide mb-3 italic">
            Video
          </h1>
          <div className="flex items-center justify-center gap-2 mb-5">
            <span className="w-6 h-px bg-border" />
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-medium">
              Contenido Audiovisual
            </p>
            <span className="w-6 h-px bg-border" />
          </div>

          {showBeta && betaLabel && (
            <div className="inline-flex items-center gap-1.5 rounded-full border border-pink-500/35 bg-pink-500/10 px-3 py-1 text-[11px] text-pink-200 mb-4">
              <MapPin className="h-3 w-3 shrink-0" />
              <span>
                Barrios CABA/GBA <strong className="font-semibold text-pink-100">Beta</strong>
                {" · "}
                {betaLabel}
              </span>
            </div>
          )}
        </header>

        {showBeta && betaVideo?.playbackId && (
          <section className="mb-10 animate-fade-in" style={{ animationDelay: "0.05s" }}>
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <MuxVideoPlayerWithCTA
                service="mejores_amigos"
                countryCode={country || "AR"}
                playbackId={betaVideo.playbackId}
                ctaUrl={betaVideo.ctaUrl}
                ctaLabel={betaVideo.ctaLabel?.es}
                title={betaVideo.title?.es}
              />
            </div>
          </section>
        )}

        {showBeta && (tierMedium?.url || tierHigh?.url || fallbackUrl) && (
          <section
            className="mb-10 space-y-3 animate-fade-in"
            style={{ animationDelay: "0.12s" }}
          >
            <p className="text-center text-sm text-muted-foreground leading-relaxed">
              {t(
                "baseTemplate.bestfriendInstagram.subtitle",
                "Acceso exclusivo a Mejores Amigos en Instagram.",
              )}
            </p>
            <div className="flex flex-col gap-2">
              {tierMedium?.url && (
                <a
                  href={tierMedium.url}
                  onClick={(e) => {
                    if (interceptMercadoPagoCta(tierMedium.url)) e.preventDefault();
                  }}
                  className="flex items-center justify-center gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm font-semibold text-amber-100 hover:bg-amber-500/20 transition-colors"
                >
                  {tierMedium.label || "Precio Medio"}
                </a>
              )}
              {tierHigh?.url && (
                <a
                  href={tierHigh.url}
                  onClick={(e) => {
                    if (interceptMercadoPagoCta(tierHigh.url)) e.preventDefault();
                  }}
                  className="flex items-center justify-center gap-2 rounded-xl border border-pink-500/40 bg-pink-500/10 px-4 py-3 text-sm font-semibold text-pink-100 hover:bg-pink-500/20 transition-colors"
                >
                  {tierHigh.label || "Precio Alto"}
                </a>
              )}
              {!tierMedium?.url && !tierHigh?.url && fallbackUrl && (
                <a
                  href={fallbackUrl}
                  onClick={(e) => {
                    if (interceptMercadoPagoCta(fallbackUrl)) e.preventDefault();
                  }}
                  className="flex items-center justify-center gap-2 rounded-xl border border-pink-500/40 bg-pink-500/15 px-4 py-3 text-sm font-semibold text-pink-100 hover:bg-pink-500/25 transition-colors"
                >
                  {t("baseTemplate.heroCtaLabelBestFriends", "Mejores Amigos")}
                </a>
              )}
            </div>
            <p className="text-center">
              <Link
                to="/invitacion-mejores-amigos"
                className="text-xs uppercase tracking-[0.15em] text-muted-foreground hover:text-foreground transition-colors"
              >
                Ver landing completa →
              </Link>
            </p>
          </section>
        )}

        <section
          className="mb-10 animate-fade-in"
          style={{ animationDelay: "0.1s" }}
        >
          <div className="prose prose-sm max-w-none text-muted-foreground space-y-4 text-center">
            <p className="text-sm leading-relaxed">
              Contenido en <strong>video</strong> de {BRAND_PERSON_NAME}. Reels, vlogs,
              tutorials y más en formato audiovisual para Instagram, TikTok y
              YouTube.
            </p>
            <p className="text-sm leading-relaxed">
              Explora mi colección completa de videos y reels.
            </p>
          </div>
        </section>

        <section
          className="mb-10 animate-fade-in"
          style={{ animationDelay: "0.2s" }}
        >
          <div className="flex justify-center gap-6">
            {socialLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center rounded-full border border-border text-muted-foreground hover:text-foreground hover:border-foreground/30 ui-transition-colors duration-200"
                aria-label={link.label}
              >
                <link.icon className="w-4 h-4" strokeWidth={1.5} />
              </a>
            ))}
          </div>
        </section>

        <div
          className="text-center animate-fade-in"
          style={{ animationDelay: "0.3s" }}
        >
          <a
            href="/videos"
            className="inline-block text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground transition-colors mb-4"
          >
            Ver Todos los Videos →
          </a>
        </div>

        <div
          className="text-center animate-fade-in"
          style={{ animationDelay: "0.4s" }}
        >
          <a
            href="/"
            className="inline-block text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground transition-colors"
          >
            ← Volver al inicio
          </a>
        </div>

        <footer
          className="mt-14 text-center animate-fade-in"
          style={{ animationDelay: "0.5s" }}
        >
          <p className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground/60">
            {`@${BRAND_HANDLE}`}
          </p>
        </footer>
      </main>
      {mpOverlay}
    </div>
  );
};

export default Video;